import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import _isEmpty from 'lodash-es/isEmpty';
import _get from 'lodash-es/get';
import Layout from 'sushiweb/Layout/Layout';
import Button from 'sushiweb/Button/Button';
import MenuItem from 'sushiweb/MenuItem/MenuItem';
import Dropdown from 'sushiweb/Dropdown/Dropdown';
import ChevronRight from 'sushiweb/Icons/all/ChevronRight';
import grey from 'sushiweb/tokens/color/grey';
import { P } from 'sushiweb/Typography/Text';

import {
  BottomScroll,
  minStartDate,
} from '../../common/helpers/inputHandlers';
import { FormCacheContext } from '../../common/Components/FormTemplate/FormTemplate';
import {
  ErrorContainer,
  InputWrapper,
  LabelWrapper,
  StyledInput,
  StyledLayout,
} from '../../../../styles';
import { genericErrorMsg } from '../../../../../../constants';
import {
  adhocProgramFormStates,
  currentFlowTypeEnum,
  cycleTypeEnum,
  gcRecipientOptions,
  gcRecipientTypeEnum,
  invalidEmailMsg,
  programRestrictionsTypes,
  programStatusEnum,
  topupTypeStatusEnum,
} from '../../../../../common/components/constants';
import { validateEmail } from '../../../../../../utility';
import Card from '../../../../../common/components/Card';
import { isConnectedToStagging } from '../../../../../../helpers';
import { getOffices } from '../../../../../../api/companyProfile';
import ZTrackingClient from '../../../../../../jumbo/ZTrackingClient';
import CreateProgramModalContext from '../../../CreateProgramModalContext';

const AdhocProgram = ({
  subTitle,
  flowType,
  planData,
  planDataFlag,
  selectedWorkspace,
  showErrorToast,
  reviewProgram,
  reviewingProgram,
  reviewProgramDetails,
  reviewProgramToEmployeesMapping,
  reviewingMapping,
  reviewedMapping,
  fetchingProgramInfo,
  fetchedProgramInfo,
  programInfo,
  getProgramInfo,
}) => {
  const {
    bodyStyle,
    footerStyle,
    stepsVisibility,
    goToNextStep,
    goToStep,
    getCachedFormData,
    updateCachedFormData,
  } = useContext(FormCacheContext);
  const { setModalSubtitle } = useContext(CreateProgramModalContext);

  const [nextStepFlag, setNextStepFlag] = useState(false);
  const [pocMappingFlag, setPocMappingFlag] = useState(false);
  const [errorValidation, setErrorValidation] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [GSTList, setGSTList] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState({});

  const [formData, setFormData] = useState({
    programId: planData?.program_id ?? '',
    email: planData?.poc_email ?? '',
    startDate: planData?.start_date ?? minStartDate,
    amount: planData?.gift_card_details?.amount ?? planData?.amount ?? '',
    gcRecipientType: planData?.gift_card_details?.recipient_type ?? '',
    gcCount: planData?.gift_card_details?.count ?? '',
    showError: false,
  });

  const updateFormData = newData => {
    setFormData(oldData => ({
      ...oldData,
      ...newData,
    }));
  };

  const isClientPoc =
    formData.gcRecipientType === gcRecipientTypeEnum.CLIENT_POC;

  // fetch GST offices, auto-select when there is exactly one
  useEffect(() => {
    getOffices()
      .then(res => {
        if (!res.status) {
          const errorMessage = _get(res, 'data.message.title', genericErrorMsg);
          showErrorToast(errorMessage);
          return;
        }
        setGSTList(res.data ?? []);
        if ((res.data ?? []).length > 0) {
          setSelectedOffice(res.data[0]);
        }
      })
      .catch(error => {
        showErrorToast('Failed to fetch office details');
        console.error(error);
      });
  }, []);

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'MODAL',
    });
    stepsVisibility(false);
    setModalSubtitle(subTitle);
  }, []);

  // prefill a drafted program
  useEffect(() => {
    const { programId = '' } = getCachedFormData();
    if (programId && programId !== '')
      getProgramInfo({
        programId,
        workspaceId: selectedWorkspace?.workspace_id,
      });
  }, []);

  useEffect(() => {
    const { programId = '' } = getCachedFormData();
    if (
      programId &&
      programId !== '' &&
      !fetchingProgramInfo &&
      fetchedProgramInfo &&
      planDataFlag === programStatusEnum.DRAFTED
    ) {
      updateFormData({
        programId: programInfo?.program_id ?? '',
        email: programInfo?.poc_email ?? '',
        startDate: programInfo?.start_date ?? minStartDate,
        amount: programInfo?.gift_card_details?.amount ?? programInfo?.amount,
        gcRecipientType: programInfo?.gift_card_details?.recipient_type ?? '',
        gcCount: programInfo?.gift_card_details?.count ?? '',
        showError: false,
      });
    }
  }, [fetchingProgramInfo]);

  // branch once the draft has been saved/reviewed
  useEffect(() => {
    if (!nextStepFlag || _isEmpty(reviewProgramDetails)) return;
    setNextStepFlag(false);

    const {
      validation_errors: validationErrors = [],
      program_id: programId,
    } = reviewProgramDetails;

    if (validationErrors?.length > 0) {
      updateFormData({ showError: true });
      setErrorValidation(validationErrors);
      return;
    }

    updateCachedFormData({ ...formData, programId });

    // Client POC: there are no employees to add. Review the program ->
    // employee mapping and jump straight to the confirm step once it resolves
    // (Step 2 is skipped). Employees: go to the upload step.
    if (isClientPoc) {
      setPocMappingFlag(true);
      reviewProgramToEmployeesMapping({
        reviewParams: {
          workspace_id: selectedWorkspace?.workspace_id,
          program_id: programId,
        },
      });
    } else {
      goToNextStep();
    }
  }, [reviewProgramDetails]);

  // advance the POC flow to the confirm step after the mapping review settles
  useEffect(() => {
    if (pocMappingFlag && !reviewingMapping) {
      setPocMappingFlag(false);
      if (reviewedMapping) goToStep(3);
    }
  }, [reviewingMapping]);

  useEffect(() => {
    const amountValid = !!formData.amount && parseInt(formData.amount, 10) > 0;
    const countValid =
      !isClientPoc ||
      (!!formData.gcCount && parseInt(formData.gcCount, 10) > 0);
    if (
      !!formData.email &&
      !!formData.startDate &&
      amountValid &&
      !!formData.gcRecipientType &&
      countValid &&
      !!(selectedOffice.office_id || isConnectedToStagging())
    ) {
      setDisabled(false);
    } else if (!disabled) setDisabled(true);
  }, [formData, selectedOffice]);

  const gstListItems = () =>
    GSTList.map(value => (
      <MenuItem value={value} label={value.gst_number} />
    ));

  const recipientLabel = gcRecipientOptions.find(
    option => option.value === formData.gcRecipientType,
  )?.label;

  const handleOfficeChange = val => setSelectedOffice(val);

  const handleEmailChange = newEmail =>
    updateFormData({ showError: false, email: newEmail });

  const handleRecipientChange = val =>
    updateFormData({
      showError: false,
      gcRecipientType: val,
      // reset count when the recipient is no longer the client POC
      gcCount: val === gcRecipientTypeEnum.CLIENT_POC ? formData.gcCount : '',
    });

  const handleIntegerChange = key => newValue => {
    const isInteger = Number.isInteger(parseInt(newValue, 10));
    const val = isInteger ? newValue : '';
    if (val === '' || val >= 0) {
      updateFormData({ showError: false, [key]: val });
    }
  };

  const handleNextBtnClick = () => {
    if (!validateEmail(formData.email)) {
      showErrorToast(invalidEmailMsg);
      return;
    }

    updateCachedFormData(formData);
    setNextStepFlag(true);

    const { programId } = getCachedFormData();
    const amount = parseFloat(formData.amount) || 0;

    const programBody = {
      name: '',
      poc_email: formData.email,
      program_id: programId ?? '',
      group_ids: [],
      start_date: formData.startDate,
      end_date: '',
      never_ends: false,
      workspace_id: selectedWorkspace?.workspace_id ?? '',
      amount,
      restrictions: {},
      topup_type: topupTypeStatusEnum.ACCUMULATING,
      cycle_type: cycleTypeEnum.ONE_TIME,
      restriction_type: programRestrictionsTypes.NO_RESTRICTION,
      flow_type: flowType,
      question_ids: [],
      gift_card_details: {
        recipient_type: formData.gcRecipientType,
        expiry_days: 0,
        count: isClientPoc ? parseInt(formData.gcCount, 10) || 0 : 0,
        amount,
      },
      office_details: {
        office_id: selectedOffice.office_id ?? 0,
      },
    };

    reviewProgram({ programBody });

    ZTrackingClient.recordClick({
      ui_source: 'SEND_EGIFT_CARD_MODAL',
      component: 'CONTINUE_BUTTON',
    });
  };

  return (
    <>
      <Card.Body style={bodyStyle}>
        <StyledLayout gap={20}>
          {GSTList?.length > 1 && (
            <>
              <Layout.Item colD={12} colT={12} colM={12}>
                <LabelWrapper>
                  <P>Select GST</P>
                </LabelWrapper>
              </Layout.Item>
              <Layout.Item colD={12} colT={12} colM={12}>
                <InputWrapper style={{ marginBottom: '1rem' }}>
                  <Dropdown
                    width='100%'
                    value={
                      selectedOffice.gst_number ?? (
                        <div style={{ color: grey.z500 }}>Select GST</div>
                      )
                    }
                    onSelect={handleOfficeChange}
                  >
                    {gstListItems()}
                  </Dropdown>
                </InputWrapper>
              </Layout.Item>
            </>
          )}
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <StyledInput
                innerWidth='100%'
                label='POC email ID'
                value={formData.email}
                onChange={handleEmailChange}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <LabelWrapper>
              <P>Gift Card Recipient</P>
            </LabelWrapper>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <Dropdown
                width='100%'
                value={
                  recipientLabel ?? (
                    <div style={{ color: grey.z500 }}>Select recipient</div>
                  )
                }
                onSelect={handleRecipientChange}
              >
                {gcRecipientOptions.map(option => (
                  <MenuItem value={option.value} label={option.label} />
                ))}
              </Dropdown>
            </InputWrapper>
          </Layout.Item>
          <Layout.Item
            colD={isClientPoc ? 6 : 12}
            colT={isClientPoc ? 6 : 12}
            colM={isClientPoc ? 6 : 12}
          >
            <InputWrapper>
              <StyledInput
                innerWidth='100%'
                label='Amount per gift card'
                value={formData.amount}
                onChange={handleIntegerChange('amount')}
              />
            </InputWrapper>
          </Layout.Item>
          {isClientPoc && (
            <Layout.Item colD={6} colT={6} colM={6}>
              <InputWrapper>
                <StyledInput
                  innerWidth='100%'
                  label='No. of gift cards'
                  value={formData.gcCount}
                  onChange={handleIntegerChange('gcCount')}
                />
              </InputWrapper>
            </Layout.Item>
          )}
        </StyledLayout>
        {formData.showError && errorValidation.length > 0 && (
          <>
            <BottomScroll />
            <ErrorContainer>
              {errorValidation.map(err => (
                <p>{err}</p>
              ))}
            </ErrorContainer>
          </>
        )}
      </Card.Body>
      <Card.Footer style={footerStyle}>
        <Button
          onClick={handleNextBtnClick}
          loading={reviewingProgram || pocMappingFlag}
          disabled={disabled}
          icon={<ChevronRight />}
          iconAlign='right'
        >
          Continue
        </Button>
      </Card.Footer>
    </>
  );
};

AdhocProgram.propTypes = {
  subTitle: PropTypes.string,
  flowType: PropTypes.oneOf(currentFlowTypeEnum),
  planData: PropTypes.objectOf(PropTypes.any),
  planDataFlag: PropTypes.oneOf(adhocProgramFormStates),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  showErrorToast: PropTypes.func,
  reviewProgram: PropTypes.func,
  reviewingProgram: PropTypes.bool,
  reviewProgramDetails: PropTypes.objectOf(PropTypes.any),
  reviewProgramToEmployeesMapping: PropTypes.func,
  reviewingMapping: PropTypes.bool,
  reviewedMapping: PropTypes.bool,
  programInfo: PropTypes.objectOf(PropTypes.any),
  getProgramInfo: PropTypes.func,
  fetchingProgramInfo: PropTypes.bool,
  fetchedProgramInfo: PropTypes.bool,
};

AdhocProgram.defaultProps = {
  subTitle: '',
  flowType: currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD,
  planData: {},
  planDataFlag: programStatusEnum.DRAFTED,
  selectedWorkspace: {},
  showErrorToast: _noop,
  reviewProgramDetails: {},
  reviewProgram: _noop,
  reviewingProgram: false,
  reviewProgramToEmployeesMapping: _noop,
  reviewingMapping: false,
  reviewedMapping: false,
  programInfo: {},
  getProgramInfo: _noop,
  fetchedProgramInfo: true,
  fetchingProgramInfo: true,
};

export default AdhocProgram;
